import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, DialogClose, IconPlus, ListTable, Notice, SelectField, TextField, type ListColumn } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { formatDate, formatMoney, t } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { EXPENSE_CATEGORIES, expenseApi, type Expense, type ExpenseCategory } from "./api.ts";
import { BackToHr, useActiveEmployees, useNames } from "./common.tsx";
import { RefuseDialog } from "./LeavePage.tsx";
import { expensesQuery, PEOPLE } from "./queries.ts";
import { localDate } from "./time.ts";

const STATE_TONE = { draft: "warning", approved: "success", refused: "danger" } as const;
type ExpenseState = keyof typeof STATE_TONE;
const stateOf = (s: string): ExpenseState => (s in STATE_TONE ? (s as ExpenseState) : "draft");

/**
 * «Заявки на витрати» (EXP-01, КД-09..КД-11): вносит и утверждает
 * «Керівництво». Утверждённая сразу пишется в «Витрати»; чек
 * прикладывается файлом, почтой не принимается.
 */
export function ExpensesPage() {
  const expenses = useQuery(expensesQuery);
  const name = useNames();
  const [open, setOpen] = useState<Expense | null>(null);
  const [creating, setCreating] = useState(false);
  const columns: ListColumn<Expense>[] = [
    { key: "who", header: t("att.employee"), kind: "title", render: (e) => name(e.employeeId) },
    { key: "date", header: t("exp.date"), kind: "text", width: "6rem", render: (e) => <span className="w-num">{formatDate(e.date)}</span> },
    { key: "category", header: t("exp.category"), kind: "text", width: "9rem", render: (e) => t(`exp.cat.${e.category}`) },
    { key: "state", header: t("exp.state"), kind: "text", width: "9rem", render: (e) => <Badge tone={STATE_TONE[stateOf(e.state)]}>{t(`exp.state.${stateOf(e.state)}`)}</Badge> },
    { key: "amount", header: t("exp.amount"), kind: "number", width: "9rem", render: (e) => formatMoney(e.amount, "UAH") },
  ];
  return (
    <div className="page people">
      <div className="page-head">
        <BackToHr />
        <h1 className="page-title">{t("exp.title")}</h1>
        <Button variant="create" onClick={() => setCreating(true)}>
          <IconPlus />
          {t("exp.new")}
        </Button>
      </div>
      {expenses.error ? <Notice tone="danger" title={errorNotice(expenses.error).title} className="stock-notice" /> : null}
      <ListTable label={t("exp.title")} columns={columns} rows={expenses.data ?? []} rowKey={(e) => String(e.id)} empty={expenses.data ? t("exp.empty") : null} onRowClick={setOpen} />
      {open ? <ExpenseDialog expense={open} onClose={() => setOpen(null)} /> : null}
      {creating ? <NewExpenseDialog onClose={() => setCreating(false)} /> : null}
    </div>
  );
}

function ExpenseDialog({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const queryClient = useQueryClient();
  const name = useNames();
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [refusing, setRefusing] = useState(false);
  const approve = useMutation({ mutationFn: () => expenseApi.approve(expense.id) });
  const refuse = useMutation({ mutationFn: (reason: string) => expenseApi.refuse(expense.id, reason) });
  const done = {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEOPLE });
      onClose();
    },
    onError: (err: unknown) => setNotice(errorNotice(err)),
  };
  const state = stateOf(expense.state);
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={`${t(`exp.cat.${expense.category}`)} · ${formatMoney(expense.amount, "UAH")}`}
      footer={
        state === "draft" ? (
          <>
            <Button variant="primary" disabled={approve.isPending} onClick={() => approve.mutate(undefined, done)}>
              {t("exp.approve")}
            </Button>
            <Button variant="ghost" onClick={() => setRefusing(true)}>
              {t("leave.refuse")}
            </Button>
          </>
        ) : (
          <DialogClose asChild>
            <Button variant="primary">{t("people.close")}</Button>
          </DialogClose>
        )
      }
    >
      {notice ? <Notice tone={notice.tone} title={notice.title} /> : null}
      <dl className="people-facts">
        <dt>{t("att.employee")}</dt>
        <dd>{name(expense.employeeId)}</dd>
        <dt>{t("exp.date")}</dt>
        <dd className="w-num">{expense.date.split("-").reverse().join(".")}</dd>
        <dt>{t("exp.description")}</dt>
        <dd>{expense.description || "—"}</dd>
        <dt>{t("exp.family")}</dt>
        <dd>{expense.family ?? t("exp.familyNone")}</dd>
        <dt>{t("exp.activity")}</dt>
        <dd>{expense.activity ?? "—"}</dd>
        <dt>{t("exp.receipt")}</dt>
        <dd>{expense.receiptFileId ? t("exp.receiptAttached") : t("exp.receiptNone")}</dd>
        <dt>{t("exp.state")}</dt>
        <dd>
          <Badge tone={STATE_TONE[state]}>{t(`exp.state.${state}`)}</Badge>
          {expense.reason ? ` ${expense.reason}` : ""}
        </dd>
      </dl>
      {state === "draft" ? <p className="w-muted">{t("exp.approveHint")}</p> : null}
      <RefuseDialog open={refusing} onClose={() => setRefusing(false)} onRefuse={(reason) => refuse.mutate(reason, done)} />
    </Dialog>
  );
}

/** Сумма в гривнах с копейками: запятая или точка, пробел тысяч не мешает. */
const parseAmount = (s: string) => Number(s.replace(/\s/g, "").replace(",", "."));

function NewExpenseDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const employees = useActiveEmployees();
  const [employeeId, setEmployeeId] = useState("");
  const [category, setCategory] = useState<ExpenseCategory>("FUEL");
  const [amount, setAmount] = useState("");
  const [day, setDay] = useState(localDate());
  const [description, setDescription] = useState("");
  const [family, setFamily] = useState("");
  const [activity, setActivity] = useState("");
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const create = useMutation({ mutationFn: expenseApi.create });
  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    create.mutate(
      { employeeId, category, amount: parseAmount(amount), date: day, description: description.trim(), family: family.trim() || null, activity: activity || null },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: PEOPLE });
          onClose();
        },
        onError: (err) => setNotice(errorNotice(err)),
      },
    );
  };
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={t("exp.new")}
      footer={
        <>
          <Button variant="primary" type="submit" form="new-expense" disabled={create.isPending || !employeeId || !amount}>
            {t("exp.create")}
          </Button>
          <DialogClose asChild>
            <Button variant="ghost">{t("common.cancel")}</Button>
          </DialogClose>
        </>
      }
    >
      <form id="new-expense" className="stock-form" onSubmit={submit}>
        {notice ? <Notice tone={notice.tone} title={notice.title} /> : null}
        <SelectField
          label={t("att.employee")}
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
          options={[{ value: "", label: t("att.employeePick"), disabled: true }, ...employees.map((x) => ({ value: x.id, label: x.name }))]}
        />
        <SelectField label={t("exp.category")} value={category} onChange={(e) => setCategory(e.target.value as ExpenseCategory)} options={EXPENSE_CATEGORIES.map((c) => ({ value: c, label: t(`exp.cat.${c}`) }))} />
        <div className="people-pair">
          <TextField label={t("exp.amountUah")} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required autoComplete="off" />
          <TextField label={t("exp.date")} type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
        </div>
        <TextField label={t("exp.description")} value={description} onChange={(e) => setDescription(e.target.value)} autoComplete="off" />
        <div className="people-pair">
          <TextField label={t("exp.family")} hint={t("exp.familyHint")} value={family} onChange={(e) => setFamily(e.target.value.toUpperCase())} autoComplete="off" />
          <SelectField
            label={t("exp.activity")}
            value={activity}
            onChange={(e) => setActivity(e.target.value)}
            options={[{ value: "", label: "—" }, ...["SER", "RND", "SRV"].map((a) => ({ value: a, label: a }))]}
          />
        </div>
        <p className="w-muted">{t("exp.receiptHint")}</p>
      </form>
    </Dialog>
  );
}
